import { useEffect, useState } from "react";
import { Check, Circle } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { recordCompletion } from "@/lib/account.functions";

interface Props {
  activationSlug: string;
  className?: string;
}

/**
 * "Mark complete" button for activations. Records a completion row per press,
 * shows today's completion count for this activation. Signed-out users see a
 * "Sign in to track" link.
 */
export function ActivationCompleteButton({ activationSlug, className }: Props) {
  const record = useServerFn(recordCompletion);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [todayCount, setTodayCount] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!userData.user) {
        setAuthed(false);
        return;
      }
      setAuthed(true);
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const { count } = await supabase
        .from("activation_completions")
        .select("id", { count: "exact", head: true })
        .eq("activation_slug", activationSlug)
        .gte("completed_at", start.toISOString());
      if (cancelled) return;
      setTodayCount(count ?? 0);
    }
    void load();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") void load();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [activationSlug]);

  if (authed === false) {
    return (
      <Link
        to="/auth"
        className={
          className ??
          "inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
        }
      >
        <Circle size={12} aria-hidden /> Sign in to track completions
      </Link>
    );
  }

  const handle = async () => {
    if (busy || !authed) return;
    setBusy(true);
    setTodayCount((c) => c + 1);
    // Fire haptic optimistically — feels immediate. Helper no-ops when
    // prefers-reduced-motion is on or the device isn't touch-capable.
    void import("@/lib/haptics").then((m) => m.haptic("success", "activation_complete")).catch(() => {});
    try {
      await record({ data: { activation_slug: activationSlug } });
    } catch {
      setTodayCount((c) => Math.max(0, c - 1));
      void import("@/lib/haptics").then((m) => m.haptic("warning", "activation_complete_error")).catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const done = todayCount > 0;
  return (
    <button
      type="button"
      onClick={handle}
      disabled={busy || authed === null}
      className={
        className ??
        `inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-colors ${
          done
            ? "border border-gold bg-gold/10 text-gold-luminous"
            : "bg-gold-gradient text-obsidian shadow-gold hover:shadow-halo"
        }`
      }
    >
      {done ? <Check size={14} /> : <Circle size={14} />}
      {done ? `Completed today · ${todayCount}` : "Mark complete"}
    </button>
  );
}
